import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { tableEditingStore } from '@/store/table-editing.store'
import { hashKey } from '@tanstack/react-query'

import { isValidUUID } from '@scrambl/shared'

import type { RecordQueryScope } from './record-query-scope'
import { isRecordQueryInScope } from './record-query-scope'

const prefixes: QueryKey[] = [['inventory'], ['analytics', 'inventory-turns']]

const WINDOW_MS = 120
const clients = new WeakMap<
  QueryClient,
  Map<string, ReturnType<typeof createEntry>>
>()

function createEntry(client: QueryClient, scope: RecordQueryScope) {
  const pending = new Map<string, QueryKey>()
  const seen = new Map<string, number>()
  let subscribers = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  let running = false
  let disposed = false
  const current = () => !disposed && !scope.signal.aborted
  const enqueue = (key: QueryKey) => pending.set(hashKey(key), key)
  const matches = (key: QueryKey, prefixes: QueryKey[]) =>
    isRecordQueryInScope(key, scope) &&
    prefixes.some(
      prefix =>
        prefix.length <= key.length &&
        hashKey(key.slice(0, prefix.length)) === hashKey(prefix),
    )

  function schedule() {
    if (!current() || timer !== undefined || running || !pending.size) return
    timer = setTimeout(flush, WINDOW_MS)
  }
  function flush() {
    timer = undefined
    if (!current() || running || !pending.size) return
    const prefixes = [...pending.values()]
    const filters = {
      predicate: (query: { queryKey: QueryKey }) =>
        matches(query.queryKey, prefixes),
    }
    if (tableEditingStore.getState().hasAnyOpenPopover()) {
      void client.invalidateQueries({ ...filters, refetchType: 'none' })
      return // Keep every prefix until the editor closes.
    }
    pending.clear()
    // An already-running fetch may have taken its snapshot before this notice.
    // Let it finish, then refresh once; never cancel/restart it for each listener.
    const alreadyFetching = client
      .getQueryCache()
      .findAll(filters)
      .filter(
        query => query.isActive() && query.state.fetchStatus === 'fetching',
      )
      .map(query => query.queryKey)
    running = true
    void client
      .invalidateQueries(
        { ...filters, refetchType: 'active' },
        { cancelRefetch: false },
      )
      .finally(() => {
        running = false
        if (!current()) return
        alreadyFetching.forEach(enqueue)
        schedule()
      })
  }
  const unsubscribe = tableEditingStore.subscribe(() => {
    if (!tableEditingStore.getState().hasAnyOpenPopover()) schedule()
  })
  const cancel = () => {
    clearTimeout(timer)
    timer = undefined
    pending.clear()
    seen.clear()
  }
  scope.signal.addEventListener('abort', cancel, { once: true })
  return {
    refreshAll() {
      if (!current()) return
      prefixes.forEach(enqueue)
      schedule()
    },
    retain() {
      subscribers++
    },
    release() {
      if (--subscribers > 0) return false
      disposed = true
      cancel()
      unsubscribe()
      scope.signal.removeEventListener('abort', cancel)
      return true
    },
    notify(eventId?: string) {
      if (!current()) return
      const now = Date.now()
      for (const [key, at] of seen) if (now - at > 30_000) seen.delete(key)
      // Older servers sent constant IDs: only deduplicate opaque commit UUIDs.
      if (eventId && isValidUUID(eventId)) {
        if (seen.has(eventId)) return
        seen.set(eventId, now)
        if (seen.size > 512) seen.delete(seen.keys().next().value!)
      }
      prefixes.forEach(enqueue)
      schedule()
    },
  }
}

/** One refresh window across detail/list listeners in the same access session. */
export function subscribeInventoryInvalidation(
  client: QueryClient,
  scope: RecordQueryScope,
) {
  let entries = clients.get(client)
  if (!entries) {
    entries = new Map()
    clients.set(client, entries)
  }
  const key = hashKey(scope.key)
  let entry = entries.get(key)
  if (!entry) {
    entry = createEntry(client, scope)
    entries.set(key, entry)
  }
  const selected = entry
  selected.retain()
  let disposed = false
  return {
    refreshAll() {
      if (!disposed) selected.refreshAll()
    },
    notify(eventId?: string) {
      if (!disposed) selected.notify(eventId)
    },
    dispose() {
      if (disposed) return
      disposed = true
      if (selected.release()) entries.delete(key)
    },
  }
}
